import { type RoleListDataFragment, type UserGroupListDataFragment } from '../../api';
import { type CompanyContextDataFragment } from '../../api/fragments/CompanyContextData';
import { type UserListDataFragment } from '../../api/fragments/UserListData';
import { type UserSpecsFragment } from '../../api/fragments/UserSpecs';

type UserWithKey = UserListDataFragment & { key: string };

export const filterUsersByCompanyContext = (
    company: Pick<CompanyContextDataFragment, 'id' | '__typename'>,
    users: UserWithKey[]
): { count: number; filteredUsers } => {
    if (!company) {
        // "All Companies" is selected
        const filteredUsers = users;
        const count = filteredUsers.length;

        return { count, filteredUsers };
    }

    if (company) {
        // A company is selected
        const filteredUsers = users.filter(user =>
            user.availableCompanies.some(availableCompany => availableCompany.id === company.id)
        );
        const count = filteredUsers.length;

        return { count, filteredUsers };
    }

    throw new Error('Company Context Error');
};

export const filteredRolesAndUserGroupsByCompanyContext = (
    company: CompanyContextDataFragment,
    roles: UserSpecsFragment['roles'],
    userGroups: UserSpecsFragment['userGroups'],
    allowedRoles: RoleListDataFragment[],
    allowedUserGroups: UserGroupListDataFragment[]
) => {
    if (!company) {
        const allowedRoleIds = new Set(allowedRoles.map(({ id }) => id));
        const allowedUserGroupIds = new Set(allowedUserGroups.map(({ id }) => id));

        const filteredRoles = roles ? roles.filter(role => allowedRoleIds.has(role.id)) : [];
        const filteredUserGroups = userGroups
            ? userGroups.filter(userGroup => allowedUserGroupIds.has(userGroup.id))
            : [];

        const rolesNotInCompany = roles ? roles.filter(role => !allowedRoleIds.has(role.id)) : [];
        const roleIdsNotInCompany = rolesNotInCompany.map(({ id }) => id);

        const userGroupsNotInCompany = userGroups
            ? userGroups.filter(userGroup => !allowedUserGroupIds.has(userGroup.id))
            : [];
        const userGroupIdsNotInCompany = userGroupsNotInCompany.map(({ id }) => id);

        return {
            filteredRoles,
            filteredUserGroups,
            roleIdsNotInCompany,
            userGroupIdsNotInCompany,
        };
    }

    if (company) {
        const filteredRoles = roles ? roles.filter(role => role.company.id === company.id) : [];
        const filteredUserGroups = userGroups
            ? userGroups.filter(userGroup => userGroup.company.id === company.id)
            : [];

        const rolesNotInCompany = roles ? roles.filter(role => role.company.id !== company.id) : [];
        const roleIdsNotInCompany = rolesNotInCompany.map(({ id }) => id);

        const userGroupsNotInCompany = userGroups ? userGroups.filter(group => group.company.id !== company.id) : [];
        const userGroupIdsNotInCompany = userGroupsNotInCompany.map(({ id }) => id);

        return { filteredRoles, filteredUserGroups, roleIdsNotInCompany, userGroupIdsNotInCompany };
    }

    throw new Error('Company Context Error');
};
